import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';

const VACATION_COLORS = { jour: '#2e7d32', nuit: '#1565c0', repos: '#9e9e9e', conge: '#f57c00', absent: '#e53935', present: '#1b5e20', permission: '#7b1fa2' };
const VACATION_LABELS = { jour: 'J', nuit: 'N', repos: 'R', conge: 'C', absent: 'A', present: 'P', permission: 'PM' };
const VACATION_BG = { jour: '#e8f5e9', nuit: '#e3f2fd', repos: '#f5f5f5', conge: '#fff3e0', absent: '#ffebee', present: '#e8f5e9', permission: '#f3e5f5' };
const VACATION_NAMES = { jour: 'Jour', nuit: 'Nuit', repos: 'Repos', conge: 'Conge', absent: 'Absent', present: 'Present', permission: 'Permission' };
const MOIS_FR = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

export default function PlanningPage() {
  // IDENTITE TENANT — ce document est REMIS AU CLIENT ou au salarie. Il portait
  // « GLADIATOR SECURITY SYSTEMS » EN DUR : l'identite de l'EDITEUR imprimee sur
  // le document d'un tenant. Meme famille que les 4 fuites corrigees en juillet-aout
  // sur les PDF backend, mais cote frontend — non couverte par la matrice du garde-fou.
  const tenant = useTenant();

  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [planning, setPlanning] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterSite, setFilterSite] = useState('');
  const [searchAgent, setSearchAgent] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editCell, setEditCell] = useState(null);

  const daysInMonth = new Date(annee, mois, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, aRes, sRes] = await Promise.all([
        api.get('/planning', { params: { mois, annee, site_id: filterSite || undefined } }),
        api.get('/agents'),
        api.get('/sites'),
      ]);
      setPlanning(pRes.data);
      setAgents(aRes.data.filter(a => a.statut === 'actif'));
      setSites(sRes.data);
    } catch {} finally { setLoading(false); }
  }, [mois, annee, filterSite]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (mois === 1) { setMois(12); setAnnee(a => a - 1); } else setMois(m => m - 1); };
  const nextMonth = () => { if (mois === 12) { setMois(1); setAnnee(a => a + 1); } else setMois(m => m + 1); };

  const generer = async () => {
    if (!window.confirm(`Generer le planning pour ${MOIS_FR[mois]} ${annee} ? Les jours deja planifies ne seront pas ecrases.`)) return;
    setGenerating(true);
    try {
      await api.post('/planning/generer', { mois, annee });
      load();
    } catch {} finally { setGenerating(false); }
  };

  const handleCellClick = (agentId, day, e) => {
    const dateStr = `${annee}-${String(mois).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (editCell && editCell.agentId === agentId && editCell.day === day) {
      setEditCell(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setEditCell({ agentId, day, dateStr, top: rect.bottom + 2, left: rect.left });
  };

  const saveCellEdit = async (vacation) => {
    if (!editCell) return;
    try {
      const siteId = filterSite || (sites.length > 0 ? sites[0].id : null);
      const heure_debut = vacation === 'jour' || vacation === 'present' ? '06:00' : vacation === 'nuit' ? '18:00' : null;
      const heure_fin = vacation === 'jour' || vacation === 'present' ? '18:00' : vacation === 'nuit' ? '06:00' : null;
      await api.post('/planning', {
        agent_id: editCell.agentId, site_id: ['repos', 'conge', 'absent', 'permission'].includes(vacation) ? null : siteId,
        date_planning: editCell.dateStr, vacation, heure_debut, heure_fin,
      });
      setEditCell(null);
      load();
    } catch {}
  };

  const clearCell = async () => {
    if (!editCell) return;
    try {
      await api.delete(`/planning/cell/${editCell.agentId}/${editCell.dateStr}`);
      setEditCell(null);
      load();
    } catch {}
  };

  const exportPdf = () => {
    const w = window.open('', '_blank', 'width=1200,height=800');
    const agentRows = filteredAgents.map(a => {
      const cells = days.map(d => {
        const dateStr = `${annee}-${String(mois).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const p = planning.find(x => x.agent_id === a.id && x.date_planning?.split('T')[0] === dateStr);
        const v = p?.vacation || '';
        const bg = VACATION_BG[v] || '#fff';
        const color = VACATION_COLORS[v] || '#ccc';
        return `<td style="background:${bg};color:${color};font-weight:700;text-align:center;padding:4px;font-size:10px">${VACATION_LABELS[v] || '-'}</td>`;
      }).join('');
      return `<tr><td style="padding:4px 8px;font-size:11px;white-space:nowrap">${a.prenom} ${a.nom}</td>${cells}</tr>`;
    }).join('');

    const headerCells = days.map(d => {
      const dow = new Date(annee, mois - 1, d).getDay();
      const bg = dow === 0 ? '#f5f5f5' : '#fff';
      return `<th style="background:${bg};padding:4px;font-size:10px;min-width:24px">${d}</th>`;
    }).join('');

    w.document.write(`<html><head><title>Planning ${MOIS_FR[mois]} ${annee}</title>
      <style>@page{size:landscape}body{font-family:Arial,sans-serif;padding:20px;font-size:11px}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd}
      .header{display:flex;justify-content:space-between;border-bottom:2px solid #8B1A1A;padding-bottom:10px;margin-bottom:16px}
      </style></head><body>
      <div class="header">
        <div><strong style="font-size:16px;color:#8B1A1A">${tenant?.nom || ''}</strong><br/>Planning mensuel</div>
        <div style="text-align:right"><strong>${MOIS_FR[mois]} ${annee}</strong></div>
      </div>
      <div style="overflow-x:auto"><table><thead><tr><th style="padding:4px 8px;text-align:left">Agent</th>${headerCells}</tr></thead><tbody>${agentRows}</tbody></table></div>
      <div style="margin-top:16px;font-size:10px;display:flex;gap:16px;flex-wrap:wrap">
        <span style="color:#2e7d32">J = Jour</span>
        <span style="color:#1565c0">N = Nuit</span>
        <span style="color:#9e9e9e">R = Repos</span>
        <span style="color:#f57c00">C = Conge</span>
        <span style="color:#e53935">A = Absent</span>
        <span style="color:#1b5e20">P = Présent</span>
        <span style="color:#7b1fa2">PM = Permission</span>
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const filteredAgents = agents.filter(a => {
    if (searchAgent && !`${a.prenom} ${a.nom}`.toLowerCase().includes(searchAgent.toLowerCase())) return false;
    if (filterSite) {
      const hasPlanning = planning.some(p => p.agent_id === a.id);
      if (!hasPlanning) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="content-card" style={{ marginBottom: '1rem' }}>
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="gss-btn-sm" onClick={prevMonth}>&larr;</button>
            <span style={{ fontWeight: 700, fontSize: 15, minWidth: 160, textAlign: 'center' }}>{MOIS_FR[mois]} {annee}</span>
            <button className="gss-btn-sm" onClick={nextMonth}>&rarr;</button>
            <select className="gss-input" style={{ width: 'auto', minWidth: 140 }} value={filterSite} onChange={e => setFilterSite(e.target.value)}>
              <option value="">Tous les sites</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
            <input className="gss-input" placeholder="Rechercher agent..." value={searchAgent} onChange={e => setSearchAgent(e.target.value)} style={{ width: 'auto', minWidth: 160 }} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="gss-btn" onClick={exportPdf}>Exporter PDF</button>
            <button className="gss-btn gss-btn-primary" onClick={generer} disabled={generating}>
              {generating ? 'Generation...' : 'Generer planning auto'}
            </button>
          </div>
        </div>
      </div>

      <div className="content-card">
        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : (
          <div className="gss-page-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: '70vh' }}>
            <div>
              <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 1400 }}>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', left: 0, background: '#f8f8f8', zIndex: 2, padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, borderBottom: '1px solid #ddd', minWidth: 150 }}>Agent</th>
                    {days.map(d => {
                      const dow = new Date(annee, mois - 1, d).getDay();
                      return (
                        <th key={d} style={{ padding: '6px 2px', textAlign: 'center', fontSize: 10, fontWeight: dow === 0 ? 700 : 500, background: dow === 0 ? '#f0f0f0' : '#f8f8f8', borderBottom: '1px solid #ddd', minWidth: 28, color: dow === 0 ? '#e53935' : '#555' }}>
                          {d}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.map(a => (
                    <tr key={a.id}>
                      <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, padding: '6px 12px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {a.prenom} {a.nom}
                      </td>
                      {days.map(d => {
                        const dateStr = `${annee}-${String(mois).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const p = planning.find(x => x.agent_id === a.id && x.date_planning?.split('T')[0] === dateStr);
                        const v = p?.vacation;
                        return (
                          <td key={d} onClick={(e) => handleCellClick(a.id, d, e)} style={{
                            padding: '4px 2px', textAlign: 'center', cursor: 'pointer', borderBottom: '1px solid #eee',
                            background: v ? VACATION_BG[v] : 'transparent',
                            color: v ? VACATION_COLORS[v] : '#ccc',
                            fontWeight: 700, fontSize: 11,
                            transition: 'all 0.1s',
                          }}>
                            {v ? VACATION_LABELS[v] : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: 'var(--gss-text-light)', flexWrap: 'wrap' }}>
          {Object.entries(VACATION_LABELS).map(([k, v]) => (
            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, background: VACATION_BG[k], border: `1px solid ${VACATION_COLORS[k]}`, display: 'inline-block' }} />
              <span style={{ color: VACATION_COLORS[k], fontWeight: 600 }}>{v}</span> = {VACATION_NAMES[k]}
            </span>
          ))}
        </div>

        {filteredAgents.length === 0 && !loading && (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Aucun agent a afficher</p>
        )}
      </div>

      {/* Inline cell dropdown */}
      {editCell && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setEditCell(null)} />
          <div style={{
            position: 'fixed', top: editCell.top, left: editCell.left, zIndex: 100,
            background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            padding: 6, display: 'flex', gap: 3, flexWrap: 'wrap', width: 220,
          }}>
            {Object.entries(VACATION_LABELS).map(([k, v]) => (
              <button key={k} onClick={() => saveCellEdit(k)} style={{
                padding: '6px 8px', background: VACATION_BG[k], color: VACATION_COLORS[k],
                border: `1px solid ${VACATION_COLORS[k]}`, fontWeight: 700, fontSize: 11,
                borderRadius: 4, cursor: 'pointer', flex: '0 0 auto',
              }}>
                {v}
              </button>
            ))}
            <button onClick={clearCell} style={{
              padding: '6px 8px', background: '#fff', color: '#e53935',
              border: '1px solid #e53935', fontWeight: 700, fontSize: 11,
              borderRadius: 4, cursor: 'pointer', flex: '1 1 100%', marginTop: 2,
            }}>
              ✕ Effacer
            </button>
          </div>
        </>
      )}
    </div>
  );
}
